import { Form, LoopButton, Modal, Typography } from '@loophealth/loop-ui-web-library';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { EditUserProfile } from '../../../assets/img';
import { Close } from '../../../assets/img/CustomIcons';
import { AttributeType } from '../../../redux/slices/PolicyListSlice/types';
import WithThemeProvider from '../../../theme/WithThemeProvider';
import { formatAmount } from '../../../utils/common';
import { IBenefitPerFamilyDetailsModal } from './types';
import { setPurchaseAttributes } from '../../../redux/slices/ModularEnrolmentSlice';
import { getDefaultSelectedPurchaseAttributes, validatePerFamilyFormSelection } from './utils';
import { validatePhoneNumber } from '../../../utils/common';
import {
    StyledDivider,
    StyledNonPerLifeSectionTier,
    StyledFieldsGrid,
    StyledNonPerLifeBenefitCard,
    StyledNonPerLifeBody,
    StyledNonPerLifeHeader,
    StyledNonPerLifeHeaderTitle,
    StyledNonPerLifeIcon,
    StyledNonPerLifeIconWrapper,
    StyledNonPerLifeModal,
    StyledNonPerLifeSection,
    StyledTotalPrice,
    StyledPlanSelectionContainer,
    StyledPlanDropdownWrapper
} from './styles';
import { StyledClose } from '../ModularFtuxModal/styles';

const BenefitPerFamilyDetailsModal: React.FC<IBenefitPerFamilyDetailsModal> = ({
    isVisible,
    setIsVisible,
    plan,
    family,
    planIds,
    handleSelectPlan,
    removeSelectPlan,
    purchaseAttributes = {},
    setSelectedItem,
    setSelectedPrice,
    handleUpdate,
    selectedItem = '',
    selectedAttributes = {},
    selectedPlanOption,
    selectedBenefits
}) => {
    const dispatch = useDispatch();
    const hasTiers = planIds.length > 1;
    if (!plan.subCategories?.length) selectedItem = plan.id;
    const [formValues, setFormValues] = useState(() =>
        getDefaultSelectedPurchaseAttributes(selectedItem, purchaseAttributes, selectedAttributes, family)
    );
    const [selectedPlan, setSelectedPlan] = useState(
        `${selectedPlanOption?.name} - ${formatAmount(selectedPlanOption?.price ?? plan.price)}` || ''
    );
    const [selectedPlanPrice, setSelectedPlanPrice] = useState(selectedPlanOption?.price || plan.price);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [showError, setShowError] = useState(false);
    // Reset form values when modal opens or selectedItem/selectedAttributes change
    useEffect(() => {
        if (isVisible) {
            const defaultValues =
                getDefaultSelectedPurchaseAttributes(selectedItem, purchaseAttributes, selectedAttributes, family);
            setFormValues(defaultValues);
            setShowError(false);
            setErrorMessage('');
        }
    }, [isVisible, selectedItem, selectedAttributes, purchaseAttributes, family]);
    const resetPurchaseAttributes = (planIds: string[]) => {
        const remainingAttributes = Object.keys(selectedAttributes).reduce((acc, key) => {
            if (!planIds.includes(key)) {
                acc[key] = selectedAttributes[key];
            }
            return acc;
        }, {});
        dispatch(setPurchaseAttributes(remainingAttributes));
    };
    const handlePlanChange = (value: string) => {
        setSelectedPlan(value);
        const selectedOption = plan.subCategories?.find((option) => option.id === value);
        if (selectedOption) {
            // flush all the previous purchase attributes
            resetPurchaseAttributes(planIds);
            setSelectedPlanPrice(selectedOption.price);
            removeSelectPlan && removeSelectPlan(selectedItem);
            setSelectedItem(selectedOption.id);
            setSelectedPrice(selectedOption.price);
        }
    };

    const clearUnselectedPurchaseAttributes = (planIds: string[]) => {
        const remainingAttributes = Object.keys(selectedAttributes).reduce((acc, key) => {
            if (!planIds.includes[key]) {
                acc[key] = selectedAttributes[key];
            }
            return acc;
        }, {});
        dispatch(setPurchaseAttributes(remainingAttributes));
    };

    const isFormValid = useMemo(() => {
        return validatePerFamilyFormSelection(purchaseAttributes, formValues, showError);
    }, [formValues, purchaseAttributes, selectedItem]);

    const handleCloseModal = () => {
        const isEditMode = selectedItem && Object.keys(selectedAttributes).includes(selectedItem);
        if (isEditMode) {
            setIsVisible(false);
            return;
        }
        if (removeSelectPlan && selectedItem) {
            removeSelectPlan(selectedItem);
            clearUnselectedPurchaseAttributes(planIds);
            setSelectedItem('');
        }
        setIsVisible(false);
    };

    const handleFormChange = (fieldKey: string, value: string, category: string) => {
        switch (category?.toLowerCase()) {
        case 'mobile':
            if (!validatePhoneNumber(value)) {
                setShowError(true);
                setErrorMessage('Invalid mobile number');
            } else {
                setShowError(false);
                setErrorMessage('');
            }
            break;
        default:
            break;
        }
        setFormValues((prev) => ({
            ...prev,
            [fieldKey]: value
        }));
    };

    const handleConfirm = () => {
        const selfMember = family.find((member) => member.relationship.toLowerCase() === 'self');
        const selfMemberId = selfMember ? selfMember.id : 'user';
        const purchaseAttributesPayload = {
            [selectedItem]: {
                [selfMemberId]: formValues
            }
        };
        const updatedAttributes = {
            ...selectedAttributes,
            ...purchaseAttributesPayload
        };
        dispatch(setPurchaseAttributes(updatedAttributes));
        if (!selectedBenefits.includes(selectedItem)) {
            handleSelectPlan(selectedItem);
            if (handleUpdate) handleUpdate(plan.id, selectedItem);
        }
        setSelectedPrice(selectedPlanPrice);
        setIsVisible(false);
    };

    const planOptions = useMemo(() => {
        return plan.subCategories?.map((subCategory) => {
            return { name: `${subCategory.name} - ${formatAmount(subCategory.price)}`, value: subCategory.id };
        });
    }, [plan.subCategories, plan]);

    return (
        <Modal
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            disablePadding
            isModalClosable={false}
            closeOnBlur={false}
            overflow="visible"
            data-testid="benefit-family-detail-modal"
        >
            <WithThemeProvider>
                <StyledNonPerLifeModal data-testid="benefit-family-detail-modal-content">
                    <StyledNonPerLifeHeader data-testid="benefit-family-detail-modal-header">
                        <StyledNonPerLifeHeaderTitle data-testid="benefit-family-detail-modal-header-title">
                            <Typography variant="large" weight="semiBold" color="tertiary">
                                Get Started with Your Benefit
                            </Typography>
                            <StyledClose
                                onClick={handleCloseModal} style={{ marginTop: '0px' }}
                                data-testid="benefit-family-detail-modal-close">
                                <Close />
                            </StyledClose>
                            <StyledNonPerLifeBenefitCard data-testid="benefit-family-detail-modal-benefit-card">
                                <StyledNonPerLifeIconWrapper data-testid="benefit-family-detail-modal-icon-wrapper">
                                    <StyledNonPerLifeIcon
                                        src={plan.logo || EditUserProfile}
                                        alt={plan.name} data-testid="benefit-family-detail-modal-icon" />
                                </StyledNonPerLifeIconWrapper>
                                <Typography variant="medium" weight="medium">
                                    {plan.name}
                                </Typography>
                            </StyledNonPerLifeBenefitCard>
                        </StyledNonPerLifeHeaderTitle>
                    </StyledNonPerLifeHeader>

                    <StyledNonPerLifeBody data-testid="benefit-family-detail-modal-body">
                        {hasTiers && (
                            <>
                                <StyledNonPerLifeSectionTier data-testid="benefit-family-detail-modal-tier-section">
                                    <StyledPlanSelectionContainer
                                        data-testid="benefit-family-detail-modal-plan-selection">
                                        <Typography variant="medium" weight="semiBold">
                                            You Selected This Plan
                                        </Typography>
                                        <StyledPlanDropdownWrapper
                                            data-testid="benefit-family-detail-modal-plan-dropdown-wrapper">
                                            <Form.FormField
                                                id="selected-plan" label=""
                                                data-testid="benefit-family-detail-modal-plan-form-field">
                                                <Form.Dropdown
                                                    placeholder={selectedPlan ? selectedPlan : 'Select plan'}
                                                    listItems={planOptions}
                                                    selectedItem={selectedItem || ''}
                                                    onClick={(value) => handlePlanChange(value)}
                                                    autoDisplayItems={false}
                                                    data-testid="benefit-family-detail-modal-plan-dropdown"
                                                />
                                            </Form.FormField>
                                        </StyledPlanDropdownWrapper>
                                    </StyledPlanSelectionContainer>
                                </StyledNonPerLifeSectionTier>
                                <StyledDivider />
                            </>
                        )}
                        {/* Required details section */}
                        <Typography variant="medium" weight="regular">
                            Add Required Details
                        </Typography>
                        <StyledNonPerLifeSection data-testid="benefit-family-detail-modal-details-section">
                            <StyledFieldsGrid data-testid="benefit-family-detail-modal-fields-grid">
                                {Object.keys(purchaseAttributes).map((fieldKey, fieldIndex) => {
                                    const attribute = purchaseAttributes[fieldKey];
                                    const items = attribute?.options ?? [];
                                    return (
                                        <Form.FormField
                                            id={fieldKey}
                                            key={fieldKey}
                                            label={attribute?.label || ''}
                                            required={attribute?.required}
                                            data-testid={`benefit-family-detail-modal-field-${fieldIndex}`}
                                        >
                                            {attribute?.type === AttributeType.SingleSelect && attribute.options && (
                                                <Form.Dropdown
                                                    searchable={true}
                                                    placeholder={attribute['placeHolder']?.toLowerCase()}
                                                    listItems={items.map((opt) => ({ name: opt, value: opt }))}
                                                    selectedItem={formValues?.[fieldKey] || ''}
                                                    onClick={(value) =>
                                                        handleFormChange(fieldKey, value, attribute.category || '')
                                                    }
                                                    autoDisplayItems={false}
                                                    data-testid={`benefit-family-detail-modal-dropdown-${fieldIndex}`}
                                                />
                                            )}

                                            {attribute?.type === AttributeType.Text && (
                                                <Form.Input
                                                    value={formValues?.[fieldKey] || ''}
                                                    onChange={(e) =>
                                                        handleFormChange(
                                                            fieldKey,
                                                            e.target.value,
                                                            attribute.category || ''
                                                        )
                                                    }
                                                    suffix={attribute?.suffix || ''}
                                                    prefix={attribute?.prefix || ''}
                                                    placeholder={attribute['placeHolder']?.toLowerCase()}
                                                    type="text"
                                                    error={showError ? errorMessage : ''}
                                                    data-testid={`benefit-family-detail-modal-input-${fieldIndex}`}
                                                />
                                            )}
                                        </Form.FormField>
                                    );
                                })}
                            </StyledFieldsGrid>
                        </StyledNonPerLifeSection>
                    </StyledNonPerLifeBody>

                    <StyledDivider />

                    {/* Total price and confirm button */}

                    <StyledTotalPrice data-testid="benefit-family-detail-modal-total-price">
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="small" color="secondary">
                                Total Price for Add-on
                            </Typography>
                            <Typography variant="title3" weight="semiBold">
                                {formatAmount(selectedPlanPrice)}
                                <Typography variant="small" color="secondary">
                                    /yr
                                </Typography>
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <LoopButton
                                variant={isFormValid ? 'filled' : 'disabled'}
                                onClick={handleConfirm} width='240px'
                                data-testid="benefit-family-detail-modal-confirm-button">
                                Confirm
                            </LoopButton>
                        </div>
                    </StyledTotalPrice>
                </StyledNonPerLifeModal>
            </WithThemeProvider>
        </Modal>
    );
};

export default BenefitPerFamilyDetailsModal;
